import { useEffect, useState } from 'react';
import {
  SEEN_EXCLUSION_OPTIONS,
  type ContributionStatsDto,
  type GenreId,
  type ProviderId,
} from '@filmsloth/shared';
import { api, type CatalogMeta } from '../api/client.js';
import { Listbox } from '../components/Listbox.js';
import { UserIcon } from '../components/icons.js';
import { toggle } from '../lib/toggle.js';
import {
  LOCALE_NAMES,
  isLocaleCode,
  locales,
  useGenreLabel,
  useI18n,
  useT,
} from '../i18n/index.js';
import { useAccount } from '../session/useAccount.js';
import { COMMUNITY_CATALOG } from '../lib/features.js';

interface AccountScreenProps {
  onBack: () => void;
  /**
   * Opens the usage dashboard. Only offered to a moderator — for everybody
   * else the endpoint behind it answers 403, and a button that cannot work is
   * worse than no button.
   */
  onOpenStats?: () => void;
}

/**
 * The account's default providers/genres, edited as chips exactly like
 * `FilterEditor` — but saved to the account immediately on every toggle
 * rather than staying local to one session, so `CreateScreen` can seed a
 * new session's filters from whatever was last touched here.
 */
function PreferencesEditor() {
  const t = useT();
  const genreLabel = useGenreLabel();
  const account = useAccount();
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .catalogMeta()
      .then(setMeta)
      .catch(() => undefined);
  }, []);

  const providers = account.account?.preferredProviders ?? [];
  const genres = account.account?.preferredGenres ?? [];

  const saveProviders = async (next: ProviderId[]) => {
    setSaving(true);
    await account.updatePreferences(next, genres);
    setSaving(false);
  };

  const saveGenres = async (next: GenreId[]) => {
    setSaving(true);
    await account.updatePreferences(providers, next);
    setSaving(false);
  };

  if (!meta) return null;

  return (
    <div className="panel stack">
      <div className="stack stack--tight">
        <h2>{t('account.preferencesTitle')}</h2>
        <p className="muted small">{t('account.preferencesHint')}</p>
      </div>

      <div className="field">
        <span className="field__label">{t('create.providers')}</span>
        <div className="chips">
          {meta.providers.map((provider) => {
            const selected = providers.includes(provider.id as ProviderId);
            return (
              <button
                key={provider.id}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                disabled={saving}
                onClick={() => void saveProviders(toggle(providers, provider.id as ProviderId))}
              >
                <span className="chip__dot" style={{ background: provider.color }} />
                {provider.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span className="field__label">{t('create.genres')}</span>
        <div className="chips">
          {meta.genres.map((genre) => {
            const selected = genres.includes(genre.id as GenreId);
            return (
              <button
                key={genre.id}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                disabled={saving}
                onClick={() => void saveGenres(toggle(genres, genre.id as GenreId))}
              >
                {genreLabel(genre.id)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * The language choice, moved here from a bar that used to sit above every
 * screen. FilmSloth already guesses a language from the browser on first
 * load (see `initialLocale` in `i18n/index.tsx`); this is only for the rare
 * case where that guess is wrong.
 */
function LanguageSettings() {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const codes = Object.keys(locales);

  if (codes.length < 2) return null;

  return (
    <div className="panel stack stack--tight">
      <h2>{t('common.language')}</h2>
      <p className="muted small">{t('account.languageHint')}</p>
      <Listbox
        value={locale}
        onChange={(code) => {
          if (isLocaleCode(code)) setLocale(code);
        }}
        ariaLabel={t('common.language')}
        options={codes.map((code) => ({ value: code, label: LOCALE_NAMES[code] ?? code }))}
      />
    </div>
  );
}

/** Credit stops accruing at 90 days out — the bar reads against that, not against whatever the current balance happens to be. */
const CAP_DAYS = 90;

/**
 * The account's own page: the address, what it has earned, and the ledger
 * behind that number.
 *
 * `/workshop/stats` is fetched here rather than folded into `useAccount`,
 * because nothing else in the app needs contribution history — carrying it
 * in the global account context would mean refetching it (or going stale)
 * every time any *other* screen touches that context.
 */
export function AccountScreen({ onBack, onOpenStats }: AccountScreenProps) {
  const t = useT();
  const account = useAccount();
  const [stats, setStats] = useState<ContributionStatsDto | null>(null);
  // Contributions and their ad-free reward belong to the community catalog;
  // with it switched off there is nothing to fetch or show.
  const [loading, setLoading] = useState(COMMUNITY_CATALOG);
  const [error, setError] = useState(false);
  const [seenExclusionDays, setSeenExclusionDays] = useState<number | null>(
    account.account?.seenExclusionDays ?? null,
  );
  const [savingSeenSetting, setSavingSeenSetting] = useState(false);

  useEffect(() => {
    setSeenExclusionDays(account.account?.seenExclusionDays ?? null);
  }, [account.account?.seenExclusionDays]);

  const handleSeenExclusionChange = async (raw: string) => {
    const value = raw === 'forever' ? null : Number(raw);
    setSeenExclusionDays(value);
    setSavingSeenSetting(true);
    try {
      await api.updateSeenExclusionDays(value);
      await account.refresh();
    } finally {
      setSavingSeenSetting(false);
    }
  };

  const load = () => {
    if (!COMMUNITY_CATALOG) return;
    setLoading(true);
    setError(false);
    api
      .contributionStats()
      .then(setStats)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const progressPercent = stats ? Math.min(100, (stats.daysLeft / CAP_DAYS) * 100) : 0;

  return (
    <div className="screen">
      <div className="row row--between">
        <h1>{t('account.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="row account__identity">
        <span className="account__avatar">
          <UserIcon size={22} />
        </span>
        <div className="stack stack--tight">
          <strong>{account.account?.email}</strong>
          <span
            className={
              account.account?.emailConfirmed ? 'account__status' : 'account__status--pending'
            }
          >
            {account.account?.emailConfirmed ? t('account.confirmed') : t('account.unconfirmed')}
          </span>
        </div>
      </div>

      <LanguageSettings />

      <div className="panel stack stack--tight">
        <h2>{t('account.seenSettingsTitle')}</h2>
        <p className="muted small">{t('account.seenSettingsHint')}</p>
        <Listbox
          value={seenExclusionDays === null ? 'forever' : String(seenExclusionDays)}
          onChange={(raw) => void handleSeenExclusionChange(raw)}
          disabled={savingSeenSetting}
          ariaLabel={t('account.seenSettingsTitle')}
          options={[
            ...SEEN_EXCLUSION_OPTIONS.map((days) => ({
              value: String(days),
              label: t('account.seenExclusionDays', { days }),
            })),
            { value: 'forever', label: t('account.seenExclusionForever') },
          ]}
        />
      </div>

      <PreferencesEditor />

      {loading ? <p className="muted">{t('common.loading')}</p> : null}

      {error ? (
        <div className="panel stack stack--tight">
          <p className="muted">{t('auth.errorGeneric')}</p>
          <button type="button" className="btn btn--ghost" onClick={load}>
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {!loading && !error && stats ? (
        <>
          <div className="panel stack account__credit">
            <div className="row account__credit-value">
              {/* `account.daysLeft` carries the whole phrase (e.g. "38 days
                  left"), not a bare unit word, so the big styled number is
                  that full string rather than a raw digit next to a label. */}
              <span className="account__credit-number">
                {t('account.daysLeft', { days: stats.daysLeft })}
              </span>
            </div>
            <span className="muted small">
              {stats.adFreeUntil
                ? t('account.adFreeUntil', {
                    date: new Date(stats.adFreeUntil).toLocaleDateString(),
                  })
                : t('account.adFreeNone')}
            </span>

            <div className="stack stack--tight">
              <div className="account__credit-bar">
                <div className="account__credit-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="row row--between account__credit-legend">
                <span className="muted small">{t('account.perContribution')}</span>
                <span className="muted small">{t('account.cap')}</span>
              </div>
            </div>
          </div>

          <div className="account__tiles">
            <div className="account__tile">
              <span className="account__tile-value">{stats.confirmed}</span>
              <span className="account__tile-label muted small">
                {t('account.confirmedContributions')}
              </span>
            </div>
            <div className="account__tile">
              <span className="account__tile-value account__tile-value--pending">{stats.open}</span>
              <span className="account__tile-label muted small">
                {t('account.openContributions')}
              </span>
            </div>
          </div>

          <p className="muted small">{t('account.payoutNote')}</p>

          <div className="panel panel--flush account__ledger">
            <h2 className="account__ledger-title">{t('account.ledger')}</h2>
            {stats.entries.length === 0 ? (
              <p className="muted account__ledger-empty">{t('account.ledgerEmpty')}</p>
            ) : (
              stats.entries.map((entry) => (
                <div key={entry.id} className="row row--between account__ledger-row">
                  <div className="stack stack--tight">
                    <span className="account__ledger-entry-title">{entry.label}</span>
                    <span className="muted small">
                      {new Date(entry.grantedAt).toLocaleDateString()} ·{' '}
                      {entry.reason === 'fill'
                        ? t('account.reasonFill')
                        : t('account.reasonConfirm')}
                    </span>
                  </div>
                  <span className="account__ledger-amount">+{entry.days}</span>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}

      {account.moderator && onOpenStats ? (
        <div className="panel stack stack--tight">
          <h2>{t('account.statsTitle')}</h2>
          <p className="muted small">{t('account.statsHint')}</p>
          <button type="button" className="btn btn--ghost btn--block" onClick={onOpenStats}>
            {t('account.statsOpen')}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className="btn btn--danger btn--block"
        onClick={() => void account.logout().then(onBack)}
      >
        {t('account.logout')}
      </button>
    </div>
  );
}
